import { useCallback, useEffect, useRef, useState } from 'react';
import {
  endVoiceCall,
  loadPriorThreadMessages,
  mintVoiceSession,
  postVoiceAuthPromote,
  postVoiceEvent,
  proxyVoiceTool,
  streamVoiceEventChannel,
  type PriorThreadItem,
  type VoiceSessionConfig,
} from '../../api';
import type { StatusEvent } from '../types/widgets';
import { useOptionalConciergeContext } from '../../ConciergeContextProvider';
import { useVoiceRecorder } from './useVoiceRecorder';
import { useWebRtcStats } from './useWebRtcStats';
import { buildServerBargeInFallbackSessionUpdate, VoiceBargeInController } from './useVoiceBargeIn';
import { getHeadersWithUniqueVisitorId } from '../../../Auth';

/**
 * Resolve the current auth headers (Authorization Bearer + x-bc-visitor-id).
 * All voice control-plane calls go through this so signed-in users have their
 * calls attributed to their userId rather than falling through to 'anonymous'.
 */
async function resolveAuthHeaders(): Promise<Record<string, string>> {
  try {
    return await getHeadersWithUniqueVisitorId();
  } catch {
    return {};
  }
}

/**
 * Voice mode call lifecycle (Plan 1 — stabilized).
 *
 * Owns:
 *  - Mic permission via `navigator.mediaDevices.getUserMedia`.
 *  - `RTCPeerConnection` + remote audio playback + `oai-events` data channel.
 *  - Session minting via `POST /voice/session`.
 *  - WebRTC SDP exchange with `https://api.openai.com/v1/realtime/calls`.
 *  - `session.update` and the FE-triggered greeting `response.create` (D-15).
 *  - 5s heartbeat to `/voice/event` + EventSource to `/voice/event-channel/:id`.
 *  - Tool proxy: on `response.function_call_arguments.done`, POST `/voice/tool`,
 *    forward narrator string back over the data channel as `function_call_output`.
 *  - End handlers: user click / WebRTC drop / tab close → POST `/voice/end`.
 *  - Mute via toggling the local audio track's `enabled` flag.
 *
 * Lifecycle contract (the bit that bit us in v1):
 *  - All resources are owned by a per-attempt `Attempt` context, not module-level
 *    refs. React 18 StrictMode dev double-mount creates two effect runs; each
 *    cleanup tears down ONLY its own attempt. There is no global "we already
 *    ended" flag that survives cross-attempt — that was the bug behind the
 *    "ghost call" / "End button does nothing" / "Call ended → voice chat starts"
 *    symptoms.
 *  - `/voice/end` is POSTed only by the attempt that actually minted a session
 *    (i.e. there is server-side state to clean up). Strict-mode dry-run cleanup
 *    skips the POST.
 *  - The owning page is informed via `onEnded` only after a real end (call
 *    was live + the user/server/drop ended it). Mount-time aborts do not
 *    trigger `onEnded`, so the "Call ended" overlay no longer flashes.
 */

export type VoicePhase = 'idle' | 'requesting-permission' | 'connecting' | 'live' | 'ending' | 'ended' | 'error';

export interface VoiceWidgetTimedEvent {
  /** Stable id for React keying — `${type}:${createdAt}:${index}`. */
  id: string;
  /** Full data-widget-* event type. */
  type: string;
  /** Widget payload (shape depends on type — narrowed by the receiving renderer). */
  data: unknown;
  /** Wall-clock when the widget landed in the FE; used to interleave with transcripts. */
  createdAt: number;
}

export interface VoiceTranscriptLine {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** True until the model emits the `.completed` / `.done` event. */
  partial: boolean;
  createdAt: number;
}

export interface UseVoiceSessionParams {
  conversationId: string;
  /** Optional PDP context — passed to the server so instructions can hint about it. */
  pdpStacklineSku?: string;
  /** Called once the call ends (any *real* end). The page typically navigates away. */
  onEnded?: (info: {
    reason: string;
    durationSec?: number;
    mastraThreadCreated?: boolean;
    /**
     * Final transcript lines at end (Issue 4b). The text chat seeds these
     * optimistically so the just-spoken turns render immediately instead of
     * waiting ~seconds for `/voice/end` to persist + the chat to refetch.
     */
    lines?: VoiceTranscriptLine[];
    /**
     * Final widget events at end. Seeded alongside `lines` (interleaved by
     * `createdAt`) so widgets render immediately on handoff rather than
     * popping in 1-2s later when the durable refetch resolves.
     */
    widgetEvents?: VoiceWidgetTimedEvent[];
  }) => void;
  /**
   * Fired the instant the model ends the call via `end_call`, BEFORE the
   * short relay drain + teardown. Lets the overlay play the end-call SFX and
   * flip UI immediately while the connection stays open ~2s so the closing
   * turn's trailing transcripts still reach the server and persist. The
   * matching `onEnded` for this path carries reason `'ended_by_model'`, so
   * the overlay skips the SFX there to avoid a double chime.
   */
  onEndCallInitiated?: () => void;
}

export interface UseVoiceSessionState {
  phase: VoicePhase;
  muted: boolean;
  /** Seconds since the call entered the 'live' phase. */
  seconds: number;
  /** Live + completed transcript lines, oldest first. */
  lines: VoiceTranscriptLine[];
  /** Last error message — the longer, actionable body (set when phase === 'error'). */
  error?: string;
  /** Short, friendly error headline for the error card (set when phase === 'error'). */
  errorTitle?: string;
  /** True while the model is producing audio (between response.created and response.done). */
  isAssistantSpeaking: boolean;
  /**
   * True while assistant audio is audibly playing on the WebRTC track (D-81).
   * Driven by `output_audio_buffer.started` / `stopped` — NOT transcript or
   * `response.done`, which can finish before playback ends.
   */
  isAssistantAudible: boolean;
  /** True while the user is speaking. */
  isUserSpeaking: boolean;
  /**
   * Count of in-flight `/voice/tool` round-trips that should drive the
   * ambient tool-call loop (D-81). Excludes `end_call` and login tools.
   */
  toolCallsInFlight: number;
  /** Latest `data-widget-*` event from the per-call SSE channel. Consumed by `TopByState`. */
  lastWidgetEvent?: { type: string; data: unknown };
  /**
   * Every `data-widget-*` event that landed during the live call, in
   * arrival order, with a wall-clock timestamp. Consumed by the
   * single-scroll overlay (see decisions/078-...) to interleave widgets
   * with transcript lines chronologically. A `data-widget-clear` event is
   * NOT appended and does NOT reset this array (decisions/092 — prior
   * widgets are persistent history the user can scroll back to; wiping them
   * mid-call caused the "widgets vanished when I scrolled up" bug).
   * `lastWidgetEvent` stays in place for callers that just want the most
   * recent widget (admin replay `NowShowing`).
   */
  widgetEvents: VoiceWidgetTimedEvent[];
  /**
   * Latest `data-status` event from the per-call SSE channel.
   * Auto-cleared 10s after it is set, or immediately on any `data-widget-*` event.
   * `TopByState` shows `ToolRunning` while this is non-null and no widget is active.
   */
  lastStatusEvent?: StatusEvent;
  /** Toggle the local mic track's `enabled` flag. */
  setMuted: (muted: boolean) => void;
  /** End the call (POST /voice/end + cleanup). Idempotent. */
  end: (reason?: string) => Promise<void>;
  /**
   * Re-run the connection from scratch after an error (used by the error
   * card's "Try again" button). Resets error + transcript state and
   * re-triggers the connect effect.
   */
  retry: () => void;
  /**
   * D-48: fired by `PromptLoginWidget` after Cognito verification
   * succeeds. POSTs `/voice/auth-promote` (so the backend promotes
   * `state.userId` and stamps `login.completed`), clears the on-screen
   * widget, and nudges the model to retry the originally-blocked tool
   * via a fresh `response.create` system note.
   */
  notifyLoginCompleted: () => Promise<void>;
  /**
   * D-48 follow-up: fired by `PromptLoginWidget` when Cognito
   * verification FAILS (wrong OTP, expired session, network error).
   * Sends a `response.create` to the model with the failure reason so
   * the model breaks the silence (it was previously gated by the
   * `tool_choice:'none'` filler response) and prompts the user to
   * re-read the code. Widget stays on the OTP step locally.
   */
  notifyLoginFailed: (reason: string) => void;
  /**
   * Send a typed user message over the realtime data channel (barge-in if
   * the assistant is mid-response), append a user transcript bubble, and
   * request a model reply.
   */
  sendText: (text: string) => void;
}

const HEARTBEAT_INTERVAL_MS = 5000;
const REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';

/**
 * D-93 liveness watchdog window. Armed by user actions that MUST produce an
 * OpenAI server event (confirmed client-VAD speech → `input_audio_buffer.
 * speech_started`; `sendText` → `response.created`). If no inbound data-channel
 * event lands within this window, the Realtime session is dead even though the
 * WebRTC transport is still healthy (root cause for `t5ebzp99dw`), and we
 * surface the recoverable "Connection lost" card instead of a frozen overlay.
 * Sized generously above OpenAI's normal sub-second event latency to stay
 * false-positive-free.
 */
const SESSION_LIVENESS_TIMEOUT_MS = 6000;

/**
 * D-93 follow-up: upper bound `retry()` waits for the prior leg's in-flight
 * `/voice/end` to commit before re-minting. Long enough for `markEnded` to
 * land on a healthy network (the end POST is usually sub-second), short enough
 * that a stalled end never makes "Try again" feel stuck.
 */
const RETRY_END_DRAIN_TIMEOUT_MS = 1500;

/**
 * Grace window held open after the model calls `end_call` before the
 * connection is fully torn down. OpenAI delivers the closing turn's
 * transcripts (the user's final utterance STT + the assistant's farewell)
 * asynchronously, a beat AFTER `end_call` fires. The old fast-path tore down
 * immediately, so those events never reached `/voice/event` and the goodbye
 * turn was lost from the durable thread (observed live: qesumy2wq6). Keeping
 * the event relay open this long lets them land + persist. The mic is muted
 * and the UI/SFX flip immediately, so the perceived end stays snappy. Mirrors
 * the existing `endCallAfterMs` delayed-teardown path used by other tools.
 */
const END_CALL_DRAIN_MS = 2000;

/**
 * Plan 7c / D-64 — instructions for the context-aware acknowledgment
 * `response.create` that replaces the standard greeting when the FE replays
 * prior thread messages into the session. Wording is verbatim from
 * [D-64 §Greeting specifics #2](../../../../../../../packages/api-rewards/src/api/services/concierge/agents/voice-chat-wiki/decisions/064-text-to-voice-context-priming.md).
 */
const CONTEXT_AWARE_ACK_INSTRUCTIONS = [
  'The shopper just opened a voice call while we were chatting in text. The',
  'prior conversation has been seeded into your context. This call is just',
  'BEGINNING — never say goodbye, "bye", or anything that ends the call, even',
  'if the prior conversation ended with a farewell. Do NOT call end_call now.',
  'In ONE short sentence (max 20 words), acknowledge the resume and offer to',
  'keep going. Do not greet by name. Do not list capabilities. Do not summarize',
  'the prior conversation. Examples (tone, not literal):',
  '"Picking up where we left off — what would you like next?"',
  '"Glad to hear from you — what can I do?"',
  '"I\'m here — what\'s the next thing?"',
].join('\n');

/** Plan 7c / D-64 — cap on prior text messages replayed into the realtime session. */
const PRIOR_THREAD_REPLAY_LIMIT = 20;

/**
 * D-69 — cap on prior tool round-trips replayed alongside text items. Keeps the
 * priming payload bounded while preserving SKU/order continuity for the typical
 * resume case ("the first two options", "what about that Plus collar").
 */
const PRIOR_THREAD_TOOL_REPLAY_LIMIT = 3;

/** Tools that must NOT increment `toolCallsInFlight` (D-81 ambient loop). */
const TOOLS_EXCLUDED_FROM_SFX_LOOP = new Set([
  'end_call',
  'prompt_login',
  'submit_phone_for_login',
  'submit_otp_for_login',
]);

/**
 * Per-attempt resource bag. One instance per useEffect setup run. Cleanup of
 * an attempt only touches its own bag; cross-attempt interference is impossible.
 */
interface Attempt {
  /** Set true by the cleanup function — stops connect() from making further state changes. */
  cancelled: boolean;
  /** Flips to true once `/voice/session` succeeds — gates whether teardown POSTs `/voice/end`. */
  minted: boolean;
  /** Flips to true once we transitioned to the live phase — gates `onEnded` notification. */
  reachedLive: boolean;
  /** Single-flight guard for end() inside this attempt. */
  closing: boolean;
  /**
   * Pending auto-end timer set by `escalate_to_support` (endCallAfterMs).
   * The model is given a chance to finish its closing line (response.done fires)
   * before we schedule this. Cleared immediately if the user speaks (barge-in)
   * or if teardown runs for any other reason.
   */
  pendingEndTimer?: ReturnType<typeof setTimeout>;
  /**
   * Ms to wait after the next response.done before ending the call.
   * Set when a tool result carries `endCallAfterMs`; consumed and cleared by
   * the response.done handler.
   */
  pendingEndAfterMs?: number;
  /**
   * Reason to pass to `teardownAttempt` (and therefore to `/voice/end`)
   * when the deferred end fires. Set alongside `pendingEndAfterMs` when
   * a tool result carries `endCallReason` (e.g. `end_call` ⇒
   * `'ended_by_model'`). When unset, the teardown falls back to the
   * legacy `'call_ended_by_tool'` reason so existing behavior for
   * `escalate_to_support` is preserved.
   */
  pendingEndReason?: string;
  /**
   * D-48 follow-up: true between `response.created` and `response.done`.
   * Used to safely defer follow-up `response.create` sends (specifically the
   * unconstrained ones from `notifyLoginCompleted` / `notifyLoginFailed`) so
   * they don't race the in-flight filler response and trigger OpenAI's
   * `conversation_already_has_active_response` error.
   *
   * Repro: live call `b7194661-...` — filler "One moment, verifying that
   * code…" was still mid-flight when Cognito finished in <2s and the
   * unconstrained follow-up went out, OpenAI rejected it, and the model
   * stayed stuck on the filler line.
   */
  responseInFlight: boolean;
  /** ID of the currently in-flight response (set on `response.created`, cleared on `response.done`). */
  activeResponseId?: string;
  /**
   * Per-user-item snapshot of the active response id at the moment that item's
   * speech started. Lets the empty-transcript guard distinguish a
   * noise-triggered response (created *after* the turn began) from a legitimate
   * response that was already streaming before the noise — so we only cancel
   * the former. Entry is deleted once consumed.
   */
  responseIdAtItemSpeechStart: Map<string, string | undefined>;
  /**
   * Response ids the empty-transcript guard cancelled because a noise turn
   * triggered them. A function-call response (e.g. `end_call`) finishes
   * generating before `response.cancel` can land, so the cancel alone is not
   * enough — `response.done` consults this set and skips executing the
   * response's function calls / call-end scheduling. Entry is deleted on
   * consume.
   */
  suppressedResponseIds: Set<string>;
  /**
   * Single-slot deferred `response.create`. Replaces an existing entry if
   * one is already pending — at runtime we only ever have ≤1 follow-up to
   * deliver (login either completes or fails, never both). Flushed by the
   * `response.done` handler. Carries a safety timer so a missing
   * response.done doesn't lock us out indefinitely.
   */
  pendingResponseCreate?: { payload: unknown; timer: ReturnType<typeof setTimeout> };
  /** Closure-local resources owned by this attempt. */
  pc?: RTCPeerConnection;
  stream?: MediaStream;
  /** Single-track MediaStream wrapping the inbound assistant audio (Plan 3b — drives recording). */
  assistantStream?: MediaStream;
  dc?: RTCDataChannel;
  audioEl?: HTMLAudioElement;
  eventSource?: EventSource;
  heartbeat?: ReturnType<typeof setInterval>;
  ticker?: ReturnType<typeof setInterval>;
  /** Wall-clock when this attempt entered 'live'. */
  liveStartMs?: number;
  /** Plan 3b — Q35: ensure we only emit `webrtc.connected` once per attempt. */
  webrtcConnectedEmitted?: boolean;
  /** Plan 3b — Q36: ensure we only emit `webrtc.first_audio` once per attempt. */
  firstAudioEmitted?: boolean;
  /** Client Silero VAD barge-in gate (noise-resistant interruption). */
  bargeIn?: VoiceBargeInController;
  /**
   * D-93 liveness watchdog: wall-clock of the last inbound OpenAI
   * data-channel event. Compared against the watchdog's `armAt` to decide
   * whether a user action (confirmed speech / `sendText`) produced any
   * server-side reaction. A dead Realtime session leaves this frozen while
   * the WebRTC transport keeps flowing.
   */
  lastServerEventAt: number;
  /** D-93: pending liveness timer; fires `declareSessionDead` if no server event arrives. */
  livenessTimer?: ReturnType<typeof setTimeout>;
}

export function useVoiceSession(params: UseVoiceSessionParams): UseVoiceSessionState {
  const { conversationId, pdpStacklineSku, onEnded, onEndCallInitiated } = params;

  // Plan 7 Part E3 — voice control-plane calls honor the host-provided
  // apiBaseUrl (ConciergeContextProvider), matching text chat, instead of
  // each call silently falling back to resolveBrandclubHostDomain(). Kept in
  // a ref so the hook's long-lived callbacks read the current value without
  // dependency-array churn; undefined (no provider) preserves the fallback.
  const conciergeCtx = useOptionalConciergeContext();
  const apiBaseUrlRef = useRef<string | undefined>(conciergeCtx?.apiBaseUrl);
  apiBaseUrlRef.current = conciergeCtx?.apiBaseUrl;

  const [phase, setPhase] = useState<VoicePhase>('idle');
  const [muted, setMutedState] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(0);
  const [lines, setLines] = useState<VoiceTranscriptLine[]>([]);
  // Mirror of `lines` for a synchronous read at teardown — the onEnded payload
  // seeds an optimistic transcript into the text chat (Issue 4b). Assigned in
  // render so it always reflects the latest committed lines when end fires.
  const linesRef = useRef<VoiceTranscriptLine[]>([]);
  linesRef.current = lines;
  const [error, setError] = useState<string | undefined>(undefined);
  // Short, friendly headline for the error card (the modal in
  // `VoiceCallOverlay`). `error` holds the longer actionable body.
  const [errorTitle, setErrorTitle] = useState<string | undefined>(undefined);
  // D-93 follow-up: the most recent fire-and-forget `/voice/end` POST. `retry()`
  // awaits this before re-minting so the dead leg's `markEnded` has committed —
  // otherwise the re-mint races its own end and the server 409s
  // ("A call with this conversationId is already active").
  const lastEndPromiseRef = useRef<Promise<unknown> | null>(null);
  // Bumped by `retry()` to force the connect effect to re-run a fresh attempt
  // without remounting the whole overlay.
  const [attemptNonce, setAttemptNonce] = useState<number>(0);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState<boolean>(false);
  const [isAssistantAudible, setIsAssistantAudible] = useState<boolean>(false);
  const isAssistantAudibleRef = useRef<boolean>(false);
  isAssistantAudibleRef.current = isAssistantAudible;
  const [isUserSpeaking, setIsUserSpeaking] = useState<boolean>(false);
  const [toolCallsInFlight, setToolCallsInFlight] = useState<number>(0);
  const [lastWidgetEvent, setLastWidgetEvent] = useState<{ type: string; data: unknown } | undefined>(undefined);
  /**
   * Chronological log of every non-clear widget event for this call.
   * Reset on `data-widget-clear`. Drives the single-scroll overlay
   * interleaving (decisions/078-...).
   */
  const [widgetEvents, setWidgetEvents] = useState<VoiceWidgetTimedEvent[]>([]);
  // Mirror of `widgetEvents` for a synchronous read at teardown — the onEnded
  // payload seeds the just-surfaced widgets into the text chat optimistically
  // (alongside `lines`) so they render immediately instead of popping in 1-2s
  // later when `/voice/end` persists + the chat refetch resolves.
  const widgetEventsRef = useRef<VoiceWidgetTimedEvent[]>([]);
  widgetEventsRef.current = widgetEvents;
  /**
   * Monotonic counter so two widgets emitted in the same `Date.now()`
   * millisecond still get unique ids (the React `key` requirement).
   */
  const widgetSeqRef = useRef<number>(0);
  const [lastStatusEvent, setLastStatusEvent] = useState<StatusEvent | undefined>(undefined);
  /** Timer ref for the 10s `data-status` auto-clear. */
  const statusClearTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * D-48 — voice mid-call login bookkeeping.
   *  - `loginGateMs`: wall-clock the very first prompt-login widget emitted
   *    for this call. Used to compute `durationMs` for the auth-promote
   *    ping + Mixpanel funnel.
   *  - `blockedToolName`: the AUTH_REQUIRED tool the model was running
   *    when the gate fired. Carried into `/voice/auth-promote` so the
   *    admin replay UI can group login flows by entry tool.
   */
  const loginContextRef = useRef<{ loginGateMs?: number; blockedToolName?: string }>({});

  // Plan 3b — exposed to `useVoiceRecorder` + `useWebRtcStats`. Only set
  // once an attempt reaches 'live' so strict-mode dry-runs don't trigger
  // recording. Cleared on teardown.
  const [pc, setPc] = useState<RTCPeerConnection | null>(null);
  const [userStream, setUserStream] = useState<MediaStream | null>(null);
  const [assistantStream, setAssistantStream] = useState<MediaStream | null>(null);
  const [liveStartMs, setLiveStartMs] = useState<number | undefined>(undefined);

  /** The attempt we expose to public actions (End button, mute, tab-close handler). */
  const activeAttemptRef = useRef<Attempt | null>(null);
  /**
   * Dev-only (D-93): when true, `handleServerEvent` drops every inbound OpenAI
   * data-channel event so the session looks dead (WebRTC transport stays up,
   * no server reactions) while `lastServerEventAt` freezes. Flipped on by the
   * `window.__voiceDebug.dropOpenAiConnection()` console hook to reproduce the
   * "Connection lost" flow on demand; reset by `retry()` so a reconnect can
   * succeed. Always false in normal operation.
   */
  const simulateSilenceRef = useRef(false);
  /** Always reflects the latest `onEnded` so a long-running attempt sees prop changes. */
  const onEndedRef = useRef<UseVoiceSessionParams['onEnded']>(onEnded);
  onEndedRef.current = onEnded;
  const onEndCallInitiatedRef = useRef<UseVoiceSessionParams['onEndCallInitiated']>(onEndCallInitiated);
  onEndCallInitiatedRef.current = onEndCallInitiated;
  /**
   * Cached auth headers (Authorization Bearer + x-bc-visitor-id) for the
   * voice control-plane calls. Without these, the backend's
   * `getUserIdBasedOnAuthOrVisitorId` falls through to `userIdToUse =
   * 'anonymous'` so the session row's owner is `'anonymous'` — which makes
   * the Plan 4c Summary view's owner-check fail with 403 even though the
   * shopper is signed in.
   *
   * Warmed asynchronously on mount and re-resolved at the start of every
   * connect attempt so a refreshed JWT is picked up.
   */
  const authHeadersRef = useRef<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    resolveAuthHeaders().then((h) => {
      if (!cancelled) authHeadersRef.current = h;
    });
    return () => {
      cancelled = true;
    };
  }, []);
  /**
   * Sync mirror of `muted` state for the greeting-gate release path. We need
   * to read mute state from inside data-channel handlers (closure) without
   * depending on stale React state.
   */
  const mutedRef = useRef<boolean>(false);
  mutedRef.current = muted;

  /**
   * Partial transcript text accumulator — keyed by role. We avoid reading from
   * `lines` (state) to merge deltas because that closure goes stale across
   * renders. Plain ref-backed strings + line ids stay correct on every fast
   * delta sequence.
   */
  const partialRef = useRef<{
    user?: { id: string; text: string };
    assistant?: { id: string; text: string };
  }>({});

  // ── helpers ────────────────────────────────────────────────────────────────

  const safeSendDataChannel = useCallback((dc: RTCDataChannel | undefined, event: any) => {
    if (!dc || dc.readyState !== 'open') return;
    try {
      dc.send(JSON.stringify(event));
      logVoiceEvent('BROWSER', typeof event?.type === 'string' ? event.type : 'unknown', event);
    } catch {
      // ignore send failures
    }
  }, []);

  /**
   * D-48 follow-up: send a `response.create` payload immediately, or queue it
   * until the in-flight response finishes. OpenAI's Realtime API rejects a
   * second `response.create` while one is active (error code
   * `conversation_already_has_active_response`), which is exactly what was
   * deadlocking the post-OTP flow when Cognito completed in <2s and the
   * unconstrained follow-up beat the filler's `response.done`.
   *
   * Single-slot — at runtime there is never more than one pending follow-up
   * per attempt. A 6s safety timer guarantees the queued payload eventually
   * fires even in the pathological case where `response.done` never arrives.
   */
  const sendOrQueueResponseCreate = useCallback(
    (attempt: Attempt, payload: unknown) => {
      if (!attempt.dc || attempt.dc.readyState !== 'open') return;
      if (!attempt.responseInFlight) {
        safeSendDataChannel(attempt.dc, payload);
        return;
      }
      if (attempt.pendingResponseCreate) {
        clearTimeout(attempt.pendingResponseCreate.timer);
      }
      const timer = setTimeout(() => {
        if (attempt.pendingResponseCreate?.timer !== timer) return;
        attempt.pendingResponseCreate = undefined;
        if (attempt.dc && attempt.dc.readyState === 'open') {
          safeSendDataChannel(attempt.dc, payload);
        }
      }, 6000);
      attempt.pendingResponseCreate = { payload, timer };
    },
    [safeSendDataChannel],
  );

  /**
   * Insert or update the partial line for `role`. The setLines updater MUST
   * stay pure — React 18 StrictMode (dev) double-invokes setState updater
   * functions to detect impurity, and the *second* invocation produces a
   * different result than the first if the updater also mutates `partialRef`.
   * That's the bug that turned every transcript into a double bubble: the
   * first run cleared `partialRef`, the second run then took the
   * "no slot — create new line" branch and appended a duplicate committed
   * line right next to the still-italic original.
   *
   * Fix shape: decide the target line id *before* calling `setLines`, keep
   * the updater purely "find by id, update; else append", and mutate
   * `partialRef` *after* the call. Deterministic on every re-invocation.
   */
  const upsertLine = useCallback((role: 'user' | 'assistant', text: string, opts: { partial: boolean }) => {
    const slot = partialRef.current[role];
    const targetId = slot ? slot.id : `${role}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

    setLines((prev) => {
      const idx = prev.findIndex((l) => l.id === targetId);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx], text, partial: opts.partial };
        return next;
      }
      const line: VoiceTranscriptLine = {
        id: targetId,
        role,
        text,
        partial: opts.partial,
        createdAt: Date.now(),
      };
      return [...prev, line];
    });

    if (opts.partial) {
      partialRef.current[role] = { id: targetId, text };
    } else {
      partialRef.current[role] = undefined;
    }
  }, []);

  /**
   * Tear down a specific attempt. `postEnd` is true only when this attempt
   * actually minted a session (so server-side state needs cleanup).
   * `notifyOnEnded` is true only for real end paths — we never fire onEnded
   * from a strict-mode dry-run.
   */
  const teardownAttempt = useCallback(
    async (attempt: Attempt, reason: string, opts: { postEnd: boolean; notifyOnEnded: boolean }) => {
      if (attempt.closing) return;
      attempt.closing = true;
      attempt.cancelled = true;

      // Clear the data-status auto-clear timer so it can't fire after the call ends.
      if (statusClearTimerRef.current !== undefined) {
        clearTimeout(statusClearTimerRef.current);
        statusClearTimerRef.current = undefined;
      }

      // Clear any pending auto-end timer (escalate_to_support / end_call).
      if (attempt.pendingEndTimer !== undefined) {
        clearTimeout(attempt.pendingEndTimer);
        attempt.pendingEndTimer = undefined;
      }
      attempt.pendingEndAfterMs = undefined;
      attempt.pendingEndReason = undefined;

      // D-48 follow-up: drop any deferred `response.create` — the data
      // channel is about to close, so flushing would no-op anyway, but
      // clearing the timer prevents a stale callback after teardown.
      if (attempt.pendingResponseCreate) {
        clearTimeout(attempt.pendingResponseCreate.timer);
        attempt.pendingResponseCreate = undefined;
      }
      attempt.responseInFlight = false;
      setIsAssistantAudible(false);

      // D-93: clear the liveness watchdog so it can't fire after teardown.
      if (attempt.livenessTimer !== undefined) {
        clearTimeout(attempt.livenessTimer);
        attempt.livenessTimer = undefined;
      }

      // End-button hardening: detach the Silero VAD up front and stop it
      // NON-BLOCKING at the very end. `vad.destroy()` (WASM teardown) can
      // stall, and an `await` here used to sit in front of the entire
      // connection teardown — so a wedged VAD could freeze the End button.
      // We null the ref now so nothing else touches it, run all synchronous
      // teardown + the optimistic close, then fire-and-forget the stop.
      const bargeIn = attempt.bargeIn;
      attempt.bargeIn = undefined;

      try {
        if (attempt.heartbeat) clearInterval(attempt.heartbeat);
        if (attempt.ticker) clearInterval(attempt.ticker);
      } catch {
        // ignore
      }
      attempt.heartbeat = undefined;
      attempt.ticker = undefined;

      try {
        attempt.eventSource?.close();
      } catch {
        // ignore
      }
      attempt.eventSource = undefined;

      try {
        attempt.dc?.close();
      } catch {
        // ignore
      }
      attempt.dc = undefined;

      try {
        attempt.pc?.getSenders().forEach((s) => {
          try {
            s.track?.stop();
          } catch {
            // ignore
          }
        });
        attempt.pc?.close();
      } catch {
        // ignore
      }
      attempt.pc = undefined;

      try {
        attempt.stream?.getTracks().forEach((t) => t.stop());
      } catch {
        // ignore
      }
      attempt.stream = undefined;
      attempt.assistantStream = undefined;

      try {
        if (attempt.audioEl) {
          attempt.audioEl.srcObject = null;
        }
      } catch {
        // ignore
      }
      attempt.audioEl = undefined;

      // Drop reference if this was the active attempt.
      if (activeAttemptRef.current === attempt) {
        activeAttemptRef.current = null;
        // Plan 3b — flip the recorder + stats hooks into their cleanup pass.
        // Setting these to null causes the hooks' effects to re-run with
        // `enabled` flipped to false, which finalizes / aborts each track.
        setPc(null);
        setUserStream(null);
        setAssistantStream(null);
        setLiveStartMs(undefined);
      }

      // D-90 — optimistic end. All the teardown above is synchronous, so the
      // call is already torn down locally. Flip phase + fire `onEnded`
      // IMMEDIATELY with a locally-computed `durationSec` instead of waiting on
      // the `/voice/end` round-trip (server bookkeeping took 4-10s and blocked
      // the UI handoff). The server still does every bit of bookkeeping below.
      //
      // Only flip phase / fire onEnded for real end paths. Strict-mode dry-run
      // cleanup uses notifyOnEnded:false so the page doesn't see a phantom end.
      if (opts.notifyOnEnded) {
        setPhase('ended');
        const durationSec =
          attempt.liveStartMs != null ? Math.max(0, Math.round((Date.now() - attempt.liveStartMs) / 1000)) : 0;
        onEndedRef.current?.({
          reason,
          durationSec,
          // mastraThreadCreated is authoritatively reconciled server-side; the
          // close handler doesn't consume it, so we omit it on the optimistic
          // path rather than guess.
          lines: linesRef.current,
          widgetEvents: widgetEventsRef.current,
        });
      }

      // Fire-and-forget the server end. `endVoiceCall` sets `keepalive: true`
      // so the POST survives even if the page unmounts/navigates right after
      // the optimistic close. Server is idempotent (returns alreadyEnded:true
      // on no state). We deliberately do NOT await it.
      if (opts.postEnd && attempt.minted) {
        // Hold onto the promise so `retry()` can await it before re-minting
        // (avoids the 409 "already active" race against this same end). We
        // still don't block teardown on it — the assignment is synchronous.
        lastEndPromiseRef.current = endVoiceCall({ apiBaseUrl: apiBaseUrlRef.current,
          conversationId,
          reason,
          headersToInject: authHeadersRef.current,
        }).catch(() => {
          // best-effort; nothing in the UI depends on the response
        });
      }

      // Non-blocking VAD teardown — runs after the overlay has already closed
      // (or flipped to the error card). Never awaited, so it can never wedge End.
      void bargeIn?.stop().catch(() => {
        // ignore — WASM teardown best-effort
      });
    },
    [conversationId],
  );

  /**
   * D-93: the Realtime session stopped responding while the WebRTC transport
   * is still up. Tear the attempt down with the recoverable `session_dropped`
   * reason and flip to the error card (mirrors `fail()` but with reconnect
   * copy). `notifyOnEnded:false` keeps the overlay open on the error card
   * instead of closing back to chat, so `retry()` can re-connect in place.
   */
  const declareSessionDead = useCallback(
    (attempt: Attempt) => {
      if (attempt.cancelled || attempt.closing) return;
      const sinceLastServerEventMs = Date.now() - attempt.lastServerEventAt;
      const diag = {
        conversationId,
        reachedLive: attempt.reachedLive,
        minted: attempt.minted,
        sinceLastServerEventMs,
        livenessTimeoutMs: SESSION_LIVENESS_TIMEOUT_MS,
      };
      // Console trace for live debugging…
      logVoiceEvent('BROWSER', 'session declared dead (D-93 liveness watchdog)', diag);
      // …and a durable row in `bc-concierge-voice-events`. Posted BEFORE
      // teardown so the call state is still active and the event is accepted
      // (teardown's `/voice/end` marks it ended a beat later). Lets us answer
      // "did the watchdog fire, and how stale was the session?" from the
      // session timeline / admin replay if this recurs.
      void postVoiceEvent({ apiBaseUrl: apiBaseUrlRef.current,
        conversationId,
        events: [{ type: 'webrtc.session_dropped_detected', payload: diag, t: Date.now() }],
        headersToInject: authHeadersRef.current,
      });
      setErrorTitle('Voice session timed out');
      setError(
        'Your voice chat stopped responding. Reconnect to pick up where you left off, or head back to start a new chat.',
      );
      void teardownAttempt(attempt, 'session_dropped', { postEnd: attempt.minted, notifyOnEnded: false });
      setPhase('error');
    },
    [conversationId, teardownAttempt],
  );

  /**
   * D-93: arm the liveness watchdog after a user action that must elicit an
   * OpenAI server event. Records `armAt`; if the timer fires and no inbound
   * data-channel event arrived since (`lastServerEventAt < armAt`), the session
   * is dead. Any qualifying server event updates `lastServerEventAt` (in
   * `handleServerEvent`), which disarms this on fire. Re-arming replaces the
   * prior timer so rapid successive actions don't stack timers.
   */
  const armLiveness = useCallback(
    (attempt: Attempt) => {
      if (attempt.cancelled || attempt.closing) return;
      if (attempt.livenessTimer !== undefined) clearTimeout(attempt.livenessTimer);
      const armAt = Date.now();
      attempt.livenessTimer = setTimeout(() => {
        attempt.livenessTimer = undefined;
        if (attempt.cancelled || attempt.closing) return;
        if (attempt.lastServerEventAt < armAt) declareSessionDead(attempt);
      }, SESSION_LIVENESS_TIMEOUT_MS);
    },
    [declareSessionDead],
  );

  // ── connection setup (per useEffect run) ───────────────────────────────────

  useEffect(() => {
    if (!conversationId) return;

    const attempt: Attempt = {
      cancelled: false,
      minted: false,
      reachedLive: false,
      closing: false,
      responseInFlight: false,
      responseIdAtItemSpeechStart: new Map(),
      suppressedResponseIds: new Set(),
      lastServerEventAt: Date.now(),
    };
    activeAttemptRef.current = attempt;

    /** Returns true if this attempt should stop and surface an error. */
    const fail = (err: unknown): true => {
      if (attempt.cancelled) return true;
      const message = err instanceof Error ? err.message : String(err ?? 'Voice mode failed');
      setErrorTitle('Voice mode unavailable');
      setError(message);
      setPhase('error');
      // If we minted, cleanup will need to POST /voice/end. The error path
      // teardown does that.
      void teardownAttempt(attempt, 'error', { postEnd: attempt.minted, notifyOnEnded: false });
      return true;
    };

    const handleFunctionCall = async (call: { name: string; call_id: string; arguments: string }) => {
      // Pretty-parse args for the dev console so the request log shows
      // `{ query: "flea collars", includeTerms: [...] }` instead of an
      // opaque JSON-string blob.
      let prettyArgs: unknown = call.arguments;
      try {
        prettyArgs = typeof call.arguments === 'string' ? JSON.parse(call.arguments) : call.arguments;
      } catch {
        // keep as-is if unparseable
      }

      // ── end_call drain-then-teardown (D-81 + goodbye-persist) ─────────────
      // The end-call SFX + 'ending' UI fire immediately (snappy perceived
      // end), but we DON'T tear down the connection right away. OpenAI sends
      // the closing turn's transcripts (final user STT + assistant farewell)
      // asynchronously a beat later; tearing down immediately dropped them
      // (they never reached `/voice/event` → the goodbye vanished from the
      // thread, observed live on qesumy2wq6). Instead we mute the mic (no new
      // input/turns), keep the data channel + event relay OPEN for
      // `END_CALL_DRAIN_MS`, then tear down — so the trailing transcripts land
      // and persist. Mirrors the existing `endCallAfterMs` delayed path. The
      // `/voice/tool` POST still fires in the background so the durable
      // `tool.end_call.completed` row + trace + Mixpanel are written.
      if (call.name === 'end_call') {
        logVoiceEvent('BROWSER', `tool request - ${call.name} (async)`, {
          conversationId,
          callId: call.call_id,
          args: prettyArgs,
        });
        void proxyVoiceTool({ apiBaseUrl: apiBaseUrlRef.current,
          conversationId,
          callId: call.call_id,
          toolName: call.name,
          args: call.arguments,
          headersToInject: authHeadersRef.current,
        })
          .then((result) => {
            logVoiceEvent('BACKEND', `tool response - ${call.name} (async)`, result);
          })
          .catch((err) => {
            logVoiceEvent('BACKEND', `tool response - ${call.name} (async)`, {
              status: 'error',
              error: err instanceof Error ? err.message : String(err),
            });
          });

        if (attempt.closing || attempt.cancelled) return;

        // Immediate: audible goodbye SFX + flip UI to 'ending'.
        onEndCallInitiatedRef.current?.();
        setPhase('ending');

        // Mute the mic so no further input is captured during the drain (the
        // model already ended — we don't want a stray utterance to spawn a new
        // turn). Disabling the track keeps it alive for clean teardown.
        try {
          attempt.stream?.getAudioTracks().forEach((t) => (t.enabled = false));
        } catch {
          // ignore
        }

        // Keep the relay open for the drain window, then tear down. Reuse
        // `pendingEndTimer` so teardown's own timer-clear handles cancellation
        // if another end path (user click / drop / unmount) fires first.
        if (attempt.pendingEndTimer !== undefined) clearTimeout(attempt.pendingEndTimer);
        attempt.pendingEndTimer = setTimeout(() => {
          attempt.pendingEndTimer = undefined;
          if (!attempt.cancelled && !attempt.closing) {
            void teardownAttempt(attempt, 'ended_by_model', { postEnd: true, notifyOnEnded: true });
          }
        }, END_CALL_DRAIN_MS);
        return;
      }

      logVoiceEvent('BROWSER', `tool request - ${call.name}`, {
        conversationId,
        callId: call.call_id,
        args: prettyArgs,
      });
      const countsForSfxLoop = !TOOLS_EXCLUDED_FROM_SFX_LOOP.has(call.name);
      if (countsForSfxLoop) {
        setToolCallsInFlight((n) => n + 1);
      }
      try {
        const result = await proxyVoiceTool({ apiBaseUrl: apiBaseUrlRef.current,
          conversationId,
          callId: call.call_id,
          toolName: call.name,
          args: call.arguments,
          headersToInject: authHeadersRef.current,
        });
        logVoiceEvent('BACKEND', `tool response - ${call.name}`, result);
        // Store endCallAfterMs so the response.done handler can schedule the
        // graceful call-end after the model finishes its closing line.
        // `endCallReason` (if present, e.g. `end_call` ⇒ `'ended_by_model'`)
        // is forwarded to the teardown so `/voice/end` records the
        // distinct hangup taxonomy.
        if (typeof result.endCallAfterMs === 'number' && result.endCallAfterMs > 0) {
          attempt.pendingEndAfterMs = result.endCallAfterMs;
          attempt.pendingEndReason =
            typeof result.endCallReason === 'string' && result.endCallReason.length > 0
              ? result.endCallReason
              : undefined;
        }
        // D-46: widgets travel inline in the tool response now (not via SSE).
        // Apply them in emit order — the FE state holds only the latest, so
        // a `clear` followed by a real widget renders the real widget; a
        // trailing `clear` alone reverts the stage to the orb. `data-status`
        // is auto-cleared on any widget event (parity with the old SSE-driven
        // behaviour).
        if (Array.isArray(result.widgets) && result.widgets.length > 0) {
          for (const w of result.widgets) {
            const widgetType = `data-widget-${w.type}`;
            setLastWidgetEvent({ type: widgetType, data: w.data });
            // Decisions/092 — a `clear` event must NOT wipe the chronological
            // scroll list. `clear` is a leftover from the pre-D-78 two-pane
            // "stage" model where it reverted the single on-screen widget to
            // the orb; in the single-scroll model (D-78) prior widgets are
            // persistent history the user can scroll back to. Wiping them
            // mid-call (a narrator-only support tool like `get_order_rewards`
            // emits `clear` with no follow-up widget) made every widget on the
            // call vanish until the post-end text chat re-rendered them from
            // Mastra memory — a jarring disappearance + a parity regression vs
            // the persisted replay (which never stores `clear`, see
            // VoicePersister). The login-flow twin of this wipe was already
            // removed in D-79; this is the tool-response path it missed.
            // `lastWidgetEvent` above still tracks the clear for admin replay.
            // All non-clear events append with a monotonic id so React keys
            // stay unique even on same-ms emits.
            if (w.type !== 'clear') {
              const seq = ++widgetSeqRef.current;
              const evt: VoiceWidgetTimedEvent = {
                id: `${widgetType}:${seq}`,
                type: widgetType,
                data: w.data,
                createdAt: Date.now(),
              };
              setWidgetEvents((prev) => [...prev, evt]);
            }
            // D-48: remember which tool surfaced the very first
            // prompt-login widget on this call so the auth-promote ping
            // can carry it. We record only the first emission (the
            // initial auth-gate) — subsequent re-emits are the model
            // forwarding spoken digits, not the original blocker.
            //
            // D-48 follow-up #10: when the model is smart enough to skip
            // the "try business-tool → get auth-required" round-trip and
            // call `prompt_login` DIRECTLY (seen on live calls
            // `5acaa5ee-...` and `1b862fcf-...`), `call.name` here is
            // `'prompt_login'` — NOT the business tool the user actually
            // wants. Capturing it would make follow-up #8 force a retry
            // of `prompt_login` itself after login completes, which
            // returns `USER_ALREADY_LOGGED_IN` and wastes a tool
            // roundtrip while the model figures out what to actually
            // call. Leaving `blockedToolName` undefined when the model
            // dialed `prompt_login` directly makes `notifyLoginCompleted`
            // fall back to its `tool_choice: 'auto'` branch with
            // instructions to pick the right business tool from
            // conversation context. The "fail-then-prompt" path
            // (business tool blocked by `BaseConciergeTool` auth gate)
            // still captures the correct tool name as before.
            if (w.type === 'prompt-login' && !loginContextRef.current.loginGateMs) {
              const triggeredByPromptLoginTool = call.name === 'prompt_login';
              loginContextRef.current = {
                loginGateMs: Date.now(),
                blockedToolName: triggeredByPromptLoginTool ? undefined : call.name,
              };
            }
          }
          if (statusClearTimerRef.current !== undefined) {
            clearTimeout(statusClearTimerRef.current);
            statusClearTimerRef.current = undefined;
          }
          setLastStatusEvent(undefined);
        }
        safeSendDataChannel(attempt.dc, {
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: call.call_id,
            output: result.narratorText ?? '',
          },
        });

        // D-48 follow-up: `submit_otp_for_login(confirmed:true)` kicks off
        // `inlineVerifyOtp` on the widget (Cognito + setOtpAuth + reloadProfile
        // ~6–10s end-to-end). If we send an unconstrained `response.create`
        // here, the model will race — speak "I'll pull up your orders" and
        // call `lookup_orders` BEFORE auth has propagated, hitting the auth
        // gate again. Live calls `77cf0c56-...`, `53d16cfd-...` exposed this.
        //
        // Fix: send ONE constrained response.create that lets the model say
        // a brief "verifying…" filler with `tool_choice: 'none'` so it
        // physically can't call any tool until the widget reports
        // verify-completed and `notifyLoginCompleted` (success) or
        // `notifyLoginFailed` (error) fires its own unconstrained
        // response.create with the appropriate next-step instructions.
        let parsedArgs: { confirmed?: unknown } | undefined;
        try {
          parsedArgs =
            typeof call.arguments === 'string'
              ? (JSON.parse(call.arguments) as { confirmed?: unknown })
              : (call.arguments as unknown as { confirmed?: unknown });
        } catch {
          parsedArgs = undefined;
        }
        const isOtpConfirm = call.name === 'submit_otp_for_login' && parsedArgs?.confirmed === true;
        // D-48 follow-up #7: route through `sendOrQueueResponseCreate`
        // (not direct `safeSendDataChannel`) so that when the model emits
        // multiple function_call items in a single response, the per-call
        // response.create sends don't race each other. Without queueing,
        // back-to-back tools (observed live: two `suggest_follow_ups`
        // calls in `82f8dda4-...`) trigger
        // `conversation_already_has_active_response` because the loop
        // above awaits the proxy result but doesn't wait for the model's
        // response.done before kicking off the next response.
        if (isOtpConfirm) {
          sendOrQueueResponseCreate(attempt, {
            type: 'response.create',
            response: {
              instructions:
                "Say ONLY 'One moment — verifying that code…' (or a brief equivalent, ≤ 6 words) and then STOP. Do NOT call any tools. Do NOT speak further until a system instruction tells you the user is signed in.",
              tool_choice: 'none',
            },
          });
        } else {
          sendOrQueueResponseCreate(attempt, { type: 'response.create' });
        }
      } catch (err) {
        logVoiceEvent('BACKEND', `tool response - ${call.name}`, {
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
        });
        // eslint-disable-next-line no-console
        console.warn('[voice] tool proxy failed', err);
        safeSendDataChannel(attempt.dc, {
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: call.call_id,
            output: 'Tool unavailable. Apologize and offer an alternative.',
          },
        });
        sendOrQueueResponseCreate(attempt, { type: 'response.create' });
      } finally {
        if (countsForSfxLoop) {
          setToolCallsInFlight((n) => Math.max(0, n - 1));
        }
      }
    };

    // Plan 3c-sideband (D-31) — Originally we removed this allowlist so the
    // sideband WebSocket would be the canonical source for OpenAI Realtime
    // events. But OpenAI's sideband endpoint returns 404
    // (`call_id_not_found`) for every call_id minted via the
    // `client_secrets` → ephemeral-key → SDP-POST flow we use, even when
    // the call_id is fresh-and-live. Multiple unresolved community
    // reports going back to Sept 2025; OpenAI's `Location` header on the
    // SDP response is itself slightly malformed (`/v1/realtime/calls/calls/`
    // doubled-segment). Until OpenAI fixes this, the FE relay below is
    // the working capture path. See [D-34] in the wiki for the full
    // diagnosis. The sideband code stays wired; when OpenAI fixes the
    // 404, this allowlist becomes redundant (and we can re-pivot then).
    const KEY_EVENTS = new Set([
      'session.created',
      'session.updated',
      'response.created',
      'response.done',
      'conversation.item.input_audio_transcription.completed',
      'response.audio_transcript.done',
      'response.output_audio_transcript.done',
      'output_audio_buffer.started',
      'output_audio_buffer.stopped',
      'input_audio_buffer.speech_started',
      'input_audio_buffer.speech_stopped',
      'error',
    ]);

    const handleServerEvent = async (event: any) => {
      if (!event || typeof event !== 'object' || typeof event.type !== 'string') return;
      if (attempt.cancelled) return;

      // Dev-only (D-93): simulate a dead Realtime session. Dropping the event
      // before the stamp below freezes `lastServerEventAt`, so an armed
      // watchdog fires `declareSessionDead` exactly as it would in the wild.
      if (simulateSilenceRef.current) return;

      // D-93 liveness watchdog: ANY inbound OpenAI data-channel event proves
      // the Realtime session is still reacting. Record it so an armed watchdog
      // (set by confirmed user speech / sendText) sees a server event after its
      // `armAt` and disarms instead of declaring the session dead.
      attempt.lastServerEventAt = Date.now();

      if (KEY_EVENTS.has(event.type)) {
        // Best-effort fire-and-forget.
        postVoiceEvent({ apiBaseUrl: apiBaseUrlRef.current, conversationId, events: [event], headersToInject: authHeadersRef.current });
      }

      // Q36 (`webrtc.first_audio`) is emitted from `pc.ontrack` — see step 2
      // of `connect()`. The data channel does NOT carry audio.delta events
      // when WebRTC is the transport (audio flows over the RTP media track),
      // so listening here would never fire.

      switch (event.type) {
        case 'input_audio_buffer.speech_started':
          setIsUserSpeaking(true);
          // Snapshot which response (if any) was already streaming when this
          // turn's speech began. The empty-transcript guard uses it to avoid
          // cancelling a legitimate response that predates the (possibly noise)
          // turn. See the `conversation.item.input_audio_transcription.completed`
          // handler below.
          if (typeof event.item_id === 'string') {
            attempt.responseIdAtItemSpeechStart.set(event.item_id, attempt.activeResponseId);
          }
          // Barge-in: if the user starts speaking, cancel any pending auto-end
          // so the call doesn't close while they're mid-sentence.
          if (attempt.pendingEndTimer !== undefined) {
            clearTimeout(attempt.pendingEndTimer);
            attempt.pendingEndTimer = undefined;
          }
          attempt.pendingEndAfterMs = undefined;
          attempt.pendingEndReason = undefined;
          break;
        case 'input_audio_buffer.speech_stopped':
          setIsUserSpeaking(false);
          break;
        case 'output_audio_buffer.started':
          setIsAssistantAudible(true);
          break;
        case 'output_audio_buffer.stopped':
          setIsAssistantAudible(false);
          break;
        case 'response.created':
          setIsAssistantSpeaking(true);
          attempt.responseInFlight = true;
          attempt.activeResponseId = typeof event.response?.id === 'string' ? event.response.id : undefined;
          // Defensive UI: if the previous assistant response was cancelled
          // (e.g. legitimate barge-in or any other race),
          // its `response.audio_transcript.done` never fires
          // and the line stays italic/partial forever. The moment a *new*
          // assistant response starts, freeze the prior partial as a
          // committed line so the transcript pane never shows a permanent
          // italic ghost beside a normal bubble.
          if (partialRef.current.assistant) {
            const stranded = partialRef.current.assistant;
            upsertLine('assistant', stranded.text, { partial: false });
          }
          break;
        case 'response.done': {
          setIsAssistantSpeaking(false);
          attempt.responseInFlight = false;
          attempt.activeResponseId = undefined;
          // D-48 follow-up: flush any deferred `response.create` now that
          // the conversation is idle. We intentionally don't loop —
          // sending two back-to-back would re-create the race. The next
          // payload (if any) waits for *its* response.done.
          const pending = attempt.pendingResponseCreate;
          if (pending && attempt.dc?.readyState === 'open') {
            clearTimeout(pending.timer);
            attempt.pendingResponseCreate = undefined;
            safeSendDataChannel(attempt.dc, pending.payload);
            // We optimistically expect a `response.created` to follow; if
            // it doesn't, responseInFlight stays false and subsequent sends
            // remain unblocked — which is the safe default.
          }
          // Defensive UI: when a response ends in any non-completed status
          // (cancelled / failed / incomplete), `response.audio_transcript.done`
          // doesn't necessarily fire and the partial line would otherwise stay
          // italic forever. Freeze it now.
          const status = event.response?.status;
          if (status && status !== 'completed' && partialRef.current.assistant) {
            const stranded = partialRef.current.assistant;
            upsertLine('assistant', stranded.text, { partial: false });
          }
          // Barge-in / cancel: generation ended but `output_audio_buffer.stopped`
          // may lag; unblock the tool loop if playback was cut short.
          if (status && status !== 'completed') {
            setIsAssistantAudible(false);
          }
          // If this response was flagged as noise-triggered by the
          // empty-transcript guard, drop it entirely: do NOT run its function
          // calls (a hallucinated `end_call` from background noise would
          // otherwise hang up the call) and do NOT schedule any call-end.
          const doneResponseId = typeof event.response?.id === 'string' ? event.response.id : undefined;
          if (doneResponseId && attempt.suppressedResponseIds.has(doneResponseId)) {
            attempt.suppressedResponseIds.delete(doneResponseId);
            break;
          }
          const items: any[] = event.response?.output ?? [];
          const calls = items.filter((it) => it?.type === 'function_call');
          for (const call of calls) {
            await handleFunctionCall(call);
          }
          // Schedule graceful call-end if a tool (e.g. escalate_to_support,
          // end_call) requested it. Only schedule on a response.done that has
          // NO `function_call` items in its output — i.e. on the model's
          // SPOKEN response after the tool returned, not on the response that
          // *contained* the function_call itself. Otherwise the delay races
          // against the model still producing its closing line (the spoken
          // response comes from a SUBSEQUENT response.done after the
          // `response.create` we send post-tool-result), and the call ends
          // before the user hears "Goodbye!". `pendingEndAfterMs` was set
          // inside `handleFunctionCall` above and survives across this
          // response.done into the next one. `pendingEndReason` (when
          // present, e.g. `'ended_by_model'` from `end_call`) feeds
          // /voice/end so admin replay + Mixpanel can distinguish hangup
          // paths; absent → fallback to `'call_ended_by_tool'` (existing
          // escalate_to_support behavior).
          if (attempt.pendingEndAfterMs !== undefined && attempt.pendingEndAfterMs > 0 && calls.length === 0) {
            const delayMs = attempt.pendingEndAfterMs;
            const reason = attempt.pendingEndReason ?? 'call_ended_by_tool';
            attempt.pendingEndAfterMs = undefined;
            attempt.pendingEndReason = undefined;
            attempt.pendingEndTimer = setTimeout(() => {
              attempt.pendingEndTimer = undefined;
              if (!attempt.cancelled && !attempt.closing) {
                void teardownAttempt(attempt, reason, { postEnd: true, notifyOnEnded: true });
              }
            }, delayMs);
          }
          break;
        }
        case 'conversation.item.input_audio_transcription.delta': {
          if (typeof event.delta === 'string') {
            const prev = partialRef.current.user?.text ?? '';
            upsertLine('user', prev + event.delta, { partial: true });
          }
          break;
        }
        case 'conversation.item.input_audio_transcription.completed': {
          const transcript = typeof event.transcript === 'string' ? event.transcript : '';
          const trimmed = transcript.trim();
          // Belt-and-suspenders empty-turn guard. Even with `semantic_vad`
          // there is a window where Whisper transcribes the committed audio
          // as empty (pure noise / cough / single-syllable filler) but
          // `create_response: true` has already triggered a model response.
          // When that happens we (a) skip rendering the empty user bubble
          // and (b) cancel the response that THIS noise turn triggered so the
          // model can't confabulate a "Sorry, didn't catch that" reply to
          // silence.
          if (!trimmed) {
            const itemId = typeof event.item_id === 'string' ? event.item_id : undefined;
            const responseAtSpeechStart = itemId
              ? attempt.responseIdAtItemSpeechStart.get(itemId)
              : undefined;
            if (itemId) attempt.responseIdAtItemSpeechStart.delete(itemId);
            // Only cancel a response that was created *after* this turn began —
            // i.e. one the noise itself triggered. A response already streaming
            // before the noise (e.g. a legitimate tool-result answer) must NOT
            // be cancelled, or the assistant gets cut off mid-sentence by
            // background noise. `response.cancel` takes ONLY `type` (+ optional
            // `event_id`); any extra field is rejected with `unknown_parameter`.
            const activeId = attempt.activeResponseId;
            const noiseTriggeredResponse = !!activeId && activeId !== responseAtSpeechStart;
            if (noiseTriggeredResponse && activeId) {
              safeSendDataChannel(attempt.dc, { type: 'response.cancel' });
              // A function-call response (e.g. a hallucinated `end_call` on
              // noise) can finish before the cancel lands, so also suppress its
              // side effects when `response.done` arrives.
              attempt.suppressedResponseIds.add(activeId);
            }
            // Also drop any partial user bubble we might have created from
            // delta events for this same noise event.
            if (partialRef.current.user) {
              setLines((prev) => prev.filter((l) => l.id !== partialRef.current.user!.id));
              partialRef.current.user = undefined;
            }
            break;
          }
          upsertLine('user', trimmed, { partial: false });
          break;
        }
        case 'response.audio_transcript.delta':
        case 'response.output_audio_transcript.delta': {
          if (typeof event.delta === 'string') {
            const prev = partialRef.current.assistant?.text ?? '';
            upsertLine('assistant', prev + event.delta, { partial: true });
          }
          break;
        }
        case 'response.audio_transcript.done':
        case 'response.output_audio_transcript.done': {
          if (typeof event.transcript === 'string') {
            upsertLine('assistant', event.transcript, { partial: false });
          }
          break;
        }
        case 'error': {
          // D-48 follow-up: stringify so the error code/message is visible
          // in browser dev tools (and remote console capture). Logging the
          // object directly was rendering as "[object Object]" in our
          // capture path, hiding `conversation_already_has_active_response`
          // and similar codes that are essential for diagnosis. The full
          // event is also POSTed to /voice/event via KEY_EVENTS for
          // server-side observability.
          let serialized = '';
          try {
            serialized = JSON.stringify(event);
          } catch {
            serialized = String(event);
          }
          // eslint-disable-next-line no-console
          console.warn('[voice] OpenAI error event', serialized, event);
          break;
        }
        default:
          break;
      }
    };

    const connect = async () => {
      // 1. Mic permission (must be on the same gesture chain as the click).
      //
      // We *explicitly* request echo-cancellation / noise-suppression /
      // auto-gain-control here. They're typically the browser default but
      // browsers (especially Chrome on macOS) only reliably engage the WebRTC
      // echo canceller when the constraints are set. Without echo cancel, the
      // model's audio bleeds from the laptop speaker into the mic and trips
      // the VAD as user speech.
      setPhase('requesting-permission');
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false,
          },
        });
      } catch (permErr) {
        if (attempt.cancelled) return;
        const described = describeMicError(permErr);
        setErrorTitle(described.title);
        setError(described.body);
        setPhase('error');
        return;
      }
      if (attempt.cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      attempt.stream = stream;

      // 2. Set up RTCPeerConnection + remote audio playback.
      setPhase('connecting');
      const pc = new RTCPeerConnection();
      attempt.pc = pc;

      // Plan 3b — wire the connection-state listener BEFORE the SDP exchange.
      // If we attach it later (after `setPhase('live')`), `pc.connectionState`
      // has already transitioned to 'connected' during SDP and the event
      // never re-fires for the current state — so Q35's `webrtc.connected`
      // emit would be silently lost. Both `webrtcConnectedEmitted` and the
      // teardown branch are tolerant of being invoked at any point after
      // `pc` is created.
      const handleConnectionStateChange = () => {
        if (attempt.cancelled) return;
        const s = pc.connectionState;
        // Q35: one-shot `webrtc.connected` per attempt → controller's
        // `markConnected` populates `tWallConnected` on the session row.
        if (s === 'connected' && !attempt.webrtcConnectedEmitted) {
          attempt.webrtcConnectedEmitted = true;
          postVoiceEvent({ apiBaseUrl: apiBaseUrlRef.current,
            conversationId,
            events: [{ type: 'webrtc.connected', t: Date.now() }],
            headersToInject: authHeadersRef.current,
          });
        }
        if (s === 'failed' || s === 'closed' || s === 'disconnected') {
          void teardownAttempt(attempt, 'webrtc_drop', { postEnd: true, notifyOnEnded: true });
        }
      };
      pc.addEventListener('connectionstatechange', handleConnectionStateChange);
      // Defensive: if the connection somehow already reached 'connected'
      // before this listener landed (timing-safe but very unlikely with the
      // current order), fire the one-shot synchronously.
      if (pc.connectionState === 'connected') handleConnectionStateChange();

      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      attempt.audioEl = audioEl;
      pc.ontrack = (e) => {
        if (attempt.cancelled) return;
        audioEl.srcObject = e.streams[0];
        // Plan 3b — capture the inbound assistant audio as a single-track
        // MediaStream so `useVoiceRecorder` can record it. We build a fresh
        // MediaStream containing just the audio track (rather than the
        // whole `e.streams[0]`) to keep the recording surface narrow and
        // independent of any future tracks (video?) the API may add.
        const audioTracks = e.streams[0]?.getAudioTracks?.() ?? [];
        if (audioTracks.length > 0 && !attempt.assistantStream) {
          try {
            const ms = new MediaStream();
            ms.addTrack(audioTracks[0]);
            attempt.assistantStream = ms;
            // Only expose to recorder hooks once we've reached live so
            // strict-mode dry-runs don't kick off recording.
            if (attempt.reachedLive) {
              setAssistantStream(ms);
            }
          } catch {
            // Some browsers throw when constructing an empty MediaStream
            // and adding a track from a different stream. Fall back to the
            // raw stream — the recorder doesn't care which wrapper it gets.
            attempt.assistantStream = e.streams[0];
            if (attempt.reachedLive) {
              setAssistantStream(e.streams[0]);
            }
          }
        }

        // Q36: emit `webrtc.first_audio` once per attempt the moment the
        // assistant audio track lands on the peer connection. Originally
        // we tried to gate this on `response.audio.delta` from the data
        // channel, but in WebRTC mode the audio chunks flow over the RTP
        // media track, NOT as `*.delta` events on the data channel — that
        // event only fires on WebSocket connections. `pc.ontrack` is the
        // WebRTC-native equivalent and ALWAYS fires before the user hears
        // a single byte of assistant audio.
        if (!attempt.firstAudioEmitted) {
          attempt.firstAudioEmitted = true;
          postVoiceEvent({ apiBaseUrl: apiBaseUrlRef.current,
            conversationId,
            events: [{ type: 'webrtc.first_audio', t: Date.now() }],
            headersToInject: authHeadersRef.current,
          });
        }
      };

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const dc = pc.createDataChannel('oai-events');
      attempt.dc = dc;

      dc.addEventListener('message', (e) => {
        try {
          const evt = JSON.parse((e as MessageEvent).data);
          logVoiceEvent('OPENAI', typeof evt?.type === 'string' ? evt.type : 'unknown', evt);
          void handleServerEvent(evt);
        } catch {
          // non-JSON event; ignore
        }
      });

      // D-93 secondary net: for the subset of dead-session cases where the data
      // channel actually closes/errors (the watchdog covers the "transport up
      // but silent" majority), surface the same recoverable card. Guarded so it
      // never fires during a normal teardown (which closes `dc` itself).
      const onDcDead = () => {
        if (attempt.cancelled || attempt.closing) return;
        declareSessionDead(attempt);
      };
      dc.addEventListener('close', onDcDead);
      dc.addEventListener('error', onDcDead);

      // 3. Mint server-side ephemeral key + session config. Refresh the
      //    cached auth headers right before mint so a freshly-refreshed JWT
      //    is picked up — `userIdToUse` is sticky on the session row, so a
      //    'anonymous'-owned mint can't be recovered after the fact.
      try {
        authHeadersRef.current = await resolveAuthHeaders();
      } catch {
        // ignore — fall back to whatever was previously warmed
      }
      let minted: Awaited<ReturnType<typeof mintVoiceSession>>;
      logVoiceEvent('BROWSER', 'session request', { conversationId, pdpStacklineSku });
      try {
        minted = await mintVoiceSession({ apiBaseUrl: apiBaseUrlRef.current,
          conversationId,
          pdpStacklineSku,
          headersToInject: authHeadersRef.current,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        // A 409 here means a prior leg for this conversationId is still marked
        // active server-side — typically a re-mint that raced its own
        // `/voice/end` (see `retry()`'s drain). Flag it distinctly so it's
        // greppable if "Try again" ever bounces again.
        const isAlreadyActive = message.includes('(409)');
        logVoiceEvent('BACKEND', 'session response - error', {
          conversationId,
          error: message,
          collision: isAlreadyActive,
        });
        return void fail(err);
      }
      if (attempt.cancelled) return;
      attempt.minted = true;
      logVoiceEvent('BACKEND', 'session response', minted);

      // 4. SDP offer/answer exchange directly with OpenAI.
      let answerSdp: string;
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const sdpResponse = await fetch(REALTIME_CALLS_URL, {
          method: 'POST',
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${minted.ephemeralKey}`,
            'Content-Type': 'application/sdp',
          },
        });
        if (!sdpResponse.ok) {
          const txt = await sdpResponse.text().catch(() => '');
          throw new Error(`OpenAI WebRTC handshake failed (${sdpResponse.status}): ${txt.slice(0, 200)}`);
        }

        // Plan 3c-sideband (D-31) — OpenAI returns the per-call id in the
        // `Location` header (e.g. `/v1/realtime/calls/rtc_xxxxx`). Forward it
        // to our backend as a one-shot event so `VoiceSidebandClient.attach`
        // can attempt to open the server-side WebSocket. Currently a no-op
        // in practice — see [D-34]: OpenAI's sideband endpoint returns 404
        // for every call_id from this mint flow. We forward the call_id
        // anyway so the day OpenAI fixes the bug, sideband starts working
        // with zero code changes (the failed attach attempts are harmless).
        try {
          const location = sdpResponse.headers.get('Location');
          const openAiCallId = location?.split('/').pop()?.trim();
          if (openAiCallId) {
            postVoiceEvent({ apiBaseUrl: apiBaseUrlRef.current,
              conversationId,
              events: [
                {
                  type: 'webrtc.openai_call_id',
                  payload: { call_id: openAiCallId },
                  t: Date.now(),
                },
              ],
              headersToInject: authHeadersRef.current,
            });
          }
        } catch {
          // ignore — call_id forwarding is best-effort
        }

        answerSdp = await sdpResponse.text();
      } catch (err) {
        return void fail(err);
      }
      if (attempt.cancelled) return;

      try {
        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      } catch (err) {
        return void fail(err);
      }
      if (attempt.cancelled) return;

      // 5. Wait for the data channel to open, then push session.update + greeting.
      try {
        await waitForDataChannelOpen(dc, 10_000);
      } catch (err) {
        return void fail(err);
      }
      if (attempt.cancelled) return;

      // Plan 7c / D-64 (+ D-69 tool round-trips) — best-effort prior-context
      // fetch. We seed the realtime session with recent text turns AND the
      // last few tool round-trips from the Mastra thread so the model resumes
      // naturally instead of greeting from scratch (and so it retains SKU /
      // order continuity for follow-ups like "compare the first two"). Failure
      // here always collapses to `[]` and the standard greeting branch fires
      // below — the helper never throws, but we keep the try/catch for
      // belt-and-braces in case a future contract change starts throwing.
      let priorItems: PriorThreadItem[] = [];
      try {
        priorItems = await loadPriorThreadMessages({ apiBaseUrl: apiBaseUrlRef.current,
          conversationId,
          limit: PRIOR_THREAD_REPLAY_LIMIT,
          toolLimit: PRIOR_THREAD_TOOL_REPLAY_LIMIT,
          headersToInject: authHeadersRef.current,
        });
      } catch {
        priorItems = [];
      }
      if (attempt.cancelled) return;

      try {
        // Strip the server-only `tracing` block before the post-creation
        // `session.update`. OpenAI accepts `tracing` ONLY at session creation
        // (it rides the SDP call request); replaying it over the data channel
        // returns `tracing_update_not_allowed`, which surfaces as an `error`
        // event and inflates the session's `hadError` flag in admin replay.
        // The config is still minted with `tracing` server-side for the
        // creation path — we only omit it from the update here.
        const { tracing: _omitTracing, ...sessionUpdate } = minted.sessionConfig as VoiceSessionConfig & {
          tracing?: unknown;
        };
        safeSendDataChannel(dc, { type: 'session.update', session: sessionUpdate });

        // Plan 7c / D-64 (text) + D-69 (tool) — replay prior conversation as
        // `conversation.item.create` items BEFORE the first `response.create`.
        // OpenAI Realtime treats these as actual prior conversation history
        // (vs `instructions` which are system context); the model believes
        // the turns happened and behaves as a continuation on follow-ups.
        // Send serially, fire-and-forget; the data channel is ordered and
        // buffers fine for the v1 cap (~20 text + ≤3 tool round-trips).
        let totalChars = 0;
        let priorTextCount = 0;
        let priorToolCount = 0;
        for (const item of priorItems) {
          if (item.kind === 'text') {
            // OpenAI Realtime content-type discriminator: user role items use
            // `input_text`, assistant role items use `output_text`. Sending
            // `text` for assistant role triggers `invalid_value: "Value must
            // be 'output_text'"` and the entire item is silently dropped —
            // verified live on c0dru2unyy. D-64 §4 (Corrected 2026-05-17)
            // captures the spec; D-69 inherits it.
            totalChars += item.text.length;
            priorTextCount += 1;
            safeSendDataChannel(dc, {
              type: 'conversation.item.create',
              item: {
                type: 'message',
                role: item.role,
                content: [{ type: item.role === 'user' ? 'input_text' : 'output_text', text: item.text }],
              },
            });
          } else {
            // D-69 — replay a prior tool round-trip as native protocol items.
            // The `call_id` links the function_call to its function_call_output
            // (OpenAI rejects function_call_output without a matching prior
            // function_call). The narrator output carries the `[INTERNAL — do
            // not speak aloud]` SKU/order block per D-27 when the Mastra row
            // was persisted post-D-69; legacy rows fall through with the
            // 300-char preview and the model still benefits from knowing the
            // tool was called.
            totalChars += item.argsJson.length + item.output.length;
            priorToolCount += 1;
            safeSendDataChannel(dc, {
              type: 'conversation.item.create',
              item: {
                type: 'function_call',
                call_id: item.callId,
                name: item.toolName,
                arguments: item.argsJson,
              },
            });
            safeSendDataChannel(dc, {
              type: 'conversation.item.create',
              item: {
                type: 'function_call_output',
                call_id: item.callId,
                output: item.output,
              },
            });
          }
        }

        if (priorItems.length > 0) {
          // Context-aware acknowledgment per D-64 §Greeting specifics #2.
          safeSendDataChannel(dc, {
            type: 'response.create',
            response: { instructions: CONTEXT_AWARE_ACK_INSTRUCTIONS },
          });
          // eslint-disable-next-line no-console
          console.info(
            `[voice] primed N=${priorTextCount} text + ${priorToolCount} tool round-trips; using context-aware acknowledgment`,
          );
          void postVoiceEvent({ apiBaseUrl: apiBaseUrlRef.current,
            conversationId,
            events: [
              {
                type: 'webrtc.priming_completed',
                t: Date.now(),
                payload: {
                  priorMessageCount: priorTextCount,
                  priorToolCallCount: priorToolCount,
                  totalChars,
                },
              },
            ],
            headersToInject: authHeadersRef.current,
          });
        } else {
          // FE-driven greeting trigger (D-15). We send a verbatim instruction
          // so the model speaks exactly the greeting string and nothing more.
          // Without the "Say exactly" framing, the model treats `instructions`
          // as a loose tone guide and improvises — adding capability listings,
          // rephrasing, or expanding the greeting (observed in smoke testing).
          safeSendDataChannel(dc, {
            type: 'response.create',
            response: {
              instructions: `Say exactly and only this sentence: "${minted.greeting}". Do not add anything else. Do not list what you can help with. Do not say any other words.`,
            },
          });
          // eslint-disable-next-line no-console
          console.info('[voice] no prior messages; using standard greeting');
        }

        // Client Silero VAD barge-in: gate assistant interruption on confirmed
        // speech so ambient noise does not auto-cut the greeting (see
        // useVoiceBargeIn.ts). Requires interrupt_response:false from mint.
        const bargeIn = new VoiceBargeInController();
        attempt.bargeIn = bargeIn;
        await bargeIn.start({
          conversationId,
          getStream: () => attempt.stream,
          getAudioEl: () => attempt.audioEl,
          getDataChannel: () => attempt.dc,
          isAssistantAudible: () => isAssistantAudibleRef.current,
          isResponseInFlight: () => attempt.responseInFlight,
          getAuthHeaders: () => authHeadersRef.current,
          sendDataChannel: (event) => safeSendDataChannel(attempt.dc, event),
          onInitFailed: () => {
            safeSendDataChannel(attempt.dc, buildServerBargeInFallbackSessionUpdate());
          },
          // D-93: confirmed user speech MUST yield an OpenAI server event;
          // arm the liveness watchdog so a dead-but-connected session is caught.
          onConfirmedUserSpeech: () => armLiveness(attempt),
          log: (category, payload) => logVoiceEvent('VAD', category, payload),
        });
      } catch {
        // ignore — connection state listeners will catch a real failure
      }

      // 6. Open the SSE event channel and start heartbeat + ticker.
      // EventSource can't set custom headers, so forward both the JWT and
      // visitor id as query params (`?token=` / `?visitorId=`); the
      // controller synthesizes the equivalent `Authorization` /
      // `x-bc-visitor-id` headers before `assertOwnership` runs. Without
      // these, the SSE handshake 403's and we lose the cosmetic mid-tool
      // status preamble (D-43 covered the signed-in case; D-46 added the
      // visitor case).
      //
      // D-46: this channel only carries `data-status` now — widgets travel
      // in the `/voice/tool` HTTP response and are applied directly in
      // `handleFunctionCall` above.
      const bearerRaw = authHeadersRef.current.Authorization ?? authHeadersRef.current.authorization;
      const accessToken = bearerRaw?.replace(/^Bearer\s+/i, '').trim() || undefined;
      const visitorId = authHeadersRef.current['x-bc-visitor-id']?.trim() || undefined;
      const es = streamVoiceEventChannel({ apiBaseUrl: apiBaseUrlRef.current, conversationId, accessToken, visitorId });
      attempt.eventSource = es;
      es.addEventListener('error', () => {
        // eslint-disable-next-line no-console
        console.warn('[voice] event channel error');
      });
      es.onmessage = (msg) => {
        if (attempt.cancelled) return;
        try {
          const evt = JSON.parse(msg.data);
          if (typeof evt?.type !== 'string') return;

          if (evt.type === 'data-status') {
            const statusEvt: StatusEvent = {
              type: 'data-status',
              data: { status: typeof evt.data?.status === 'string' ? evt.data.status : 'Thinking…' },
            };
            setLastStatusEvent(statusEvt);
            // Restart the 10s auto-clear timer on each new status ping.
            if (statusClearTimerRef.current !== undefined) {
              clearTimeout(statusClearTimerRef.current);
            }
            statusClearTimerRef.current = setTimeout(() => {
              setLastStatusEvent(undefined);
              statusClearTimerRef.current = undefined;
            }, 10_000);
          }
          // Other event types (`voice-channel-ready`, anything legacy) are
          // ignored. `data-widget-*` is no longer expected on this channel
          // (D-46) — if one ever leaks through (e.g. old build of the
          // backend), we deliberately drop it; the source of truth for
          // widgets is now the tool HTTP response.
        } catch {
          // ignore non-JSON heartbeats
        }
      };

      attempt.liveStartMs = Date.now();
      attempt.reachedLive = true;
      setPhase('live');
      setSeconds(0);

      // Plan 3b — surface call-scoped resources to the recorder + stats hooks.
      // Setting these AFTER `reachedLive=true` ensures strict-mode dry-runs
      // never trigger recording. The recorder hook owns its own per-attempt
      // teardown and is idempotent on re-renders.
      setPc(pc);
      setUserStream(stream);
      setLiveStartMs(attempt.liveStartMs);
      if (attempt.assistantStream) {
        // The track may already have arrived via `pc.ontrack` before this point.
        setAssistantStream(attempt.assistantStream);
      }

      attempt.ticker = setInterval(() => {
        if (attempt.cancelled || !attempt.liveStartMs) return;
        setSeconds(Math.floor((Date.now() - attempt.liveStartMs) / 1000));
      }, 1000);

      attempt.heartbeat = setInterval(() => {
        if (attempt.cancelled) return;
        postVoiceEvent({ apiBaseUrl: apiBaseUrlRef.current,
          conversationId,
          events: [{ type: 'browser.heartbeat', t: Date.now() }],
          headersToInject: authHeadersRef.current,
        });
      }, HEARTBEAT_INTERVAL_MS);

      // (`connectionstatechange` listener was attached up-front, right after
      //  `pc = new RTCPeerConnection()` — see the Plan 3b note in step 2 for
      //  why it must land before SDP exchange. Drop / connect handling is
      //  identical to what used to live here.)
    };

    void connect();

    return () => {
      // Per-attempt cleanup. `postEnd` is gated on whether *this* attempt
      // minted a session — so strict-mode dry-runs (which abort before the mint
      // resolves) do NOT POST /voice/end and do NOT fire onEnded.
      void teardownAttempt(attempt, 'unmount', {
        postEnd: attempt.minted,
        notifyOnEnded: attempt.reachedLive,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, attemptNonce]);

  // ── Plan 3b — recording + WebRTC stats ─────────────────────────────────────
  //
  // Both hooks gate on `phase === 'live'` and read the per-call resources
  // exposed above. Strict-mode dev double-mount: each effect runs twice,
  // each cleanup tears down its own attempt. Recording starts only after
  // an attempt actually reaches live (the `setPc/setUserStream/...` calls
  // happen inside the attempt's `connect()` after `setPhase('live')`).
  useVoiceRecorder({
    conversationId,
    userStream,
    assistantStream,
    enabled: phase === 'live',
    liveStartMs,
  });
  useWebRtcStats({
    conversationId,
    pc,
    enabled: phase === 'live',
  });

  // ── tab close ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const handler = () => {
      const a = activeAttemptRef.current;
      if (!a) return;
      void teardownAttempt(a, 'tab_close', { postEnd: a.minted, notifyOnEnded: false });
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [teardownAttempt]);

  // ── dev console hook (D-93) ─────────────────────────────────────────────────
  //
  // Reproduce the "OpenAI dropped the connection" flow on demand without
  // waiting for the rare (~1 in 290) real occurrence. From the browser console
  // during a LIVE call:
  //
  //   window.__voiceDebug.dropOpenAiConnection()            // faithful: go
  //       // silent + arm the watchdog; the "Connection lost" card appears in
  //       // ~6s exactly as in the wild.
  //   window.__voiceDebug.dropOpenAiConnection({ mode: 'instant' })  // shortcut
  //       // straight to the card (skips the 6s watchdog wait).
  //
  // Then exercise the fixes: "Try again" should reconnect in place (no 409,
  // no homepage), "Back to chat" should land on the text chat (not a blank
  // home) because the call had connected. Only ever acts on the caller's own
  // active call, so it is harmless if it ships; gate/remove before GA if
  // desired.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const debug = {
      dropOpenAiConnection: (opts?: { mode?: 'instant' | 'watchdog' }) => {
        const a = activeAttemptRef.current;
        if (!a || !a.reachedLive) {
          // eslint-disable-next-line no-console
          console.warn('[voiceDebug] no live voice call to drop — start a call first.');
          return;
        }
        const mode = opts?.mode ?? 'watchdog';
        if (mode === 'instant') {
          // eslint-disable-next-line no-console
          console.info('[voiceDebug] declaring session dead immediately (instant mode).');
          declareSessionDead(a);
          return;
        }
        // eslint-disable-next-line no-console
        console.info(
          `[voiceDebug] simulating OpenAI silence — watchdog will declare the session dead in ~${SESSION_LIVENESS_TIMEOUT_MS}ms.`,
        );
        simulateSilenceRef.current = true;
        armLiveness(a);
      },
    };
    (window as unknown as Record<string, unknown>).__voiceDebug = debug;
    return () => {
      if ((window as unknown as Record<string, unknown>).__voiceDebug === debug) {
        delete (window as unknown as Record<string, unknown>).__voiceDebug;
      }
    };
  }, [declareSessionDead, armLiveness]);

  // ── public actions ─────────────────────────────────────────────────────────

  const setMuted = useCallback((next: boolean) => {
    setMutedState(next);
    mutedRef.current = next;
    const a = activeAttemptRef.current;
    if (!a?.stream) return;
    a.stream.getAudioTracks().forEach((track) => {
      track.enabled = !next;
    });
  }, []);

  const end = useCallback(
    async (reason?: string) => {
      const a = activeAttemptRef.current;
      if (!a) return;
      setPhase('ending');
      await teardownAttempt(a, reason ?? 'user_ended', {
        postEnd: a.minted,
        notifyOnEnded: a.reachedLive,
      });
    },
    [teardownAttempt],
  );

  /**
   * Re-run the connection from scratch after an error (mic permission
   * denied/dismissed, no device, device-in-use, transient connect/mint
   * failure, or a D-93 `session_dropped` dead session). Surfaced as the error
   * card's "Try again" button so the user doesn't have to close + re-open the
   * whole overlay. Resets the error + transcript/widget state, then bumps
   * `attemptNonce` to re-trigger the connect effect (whose cleanup tears down
   * the failed attempt first).
   *
   * D-93 follow-up: for the `session_dropped` path the prior leg's teardown
   * fired `/voice/end` fire-and-forget. We await that (bounded by
   * `RETRY_END_DRAIN_TIMEOUT_MS`) before bumping the nonce so the dead leg's
   * `markEnded` has committed — otherwise the re-mint races its own end and the
   * server 409s.
   */
  const retry = useCallback(() => {
    logVoiceEvent('BROWSER', 'retry reconnect requested (D-93)', {
      conversationId,
      hadPendingEnd: lastEndPromiseRef.current != null,
    });
    // Dev-only: clear any simulated silence so the reconnect can reach live.
    simulateSilenceRef.current = false;
    setError(undefined);
    setErrorTitle(undefined);
    setLines([]);
    setWidgetEvents([]);
    setLastWidgetEvent(undefined);
    setPhase('connecting');

    const pendingEnd = lastEndPromiseRef.current;
    const bump = () => {
      lastEndPromiseRef.current = null;
      setAttemptNonce((n) => n + 1);
    };
    if (!pendingEnd) {
      bump();
      return;
    }
    // Wait for the in-flight end, but never hang the retry on it.
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, RETRY_END_DRAIN_TIMEOUT_MS));
    void Promise.race([pendingEnd, timeout]).then(bump);
  }, [conversationId]);

  /**
   * D-48: fired by the on-screen `PromptLoginWidget` after Cognito
   * verification succeeds. Three jobs, in order:
   *   1. POST `/voice/auth-promote` with refreshed auth headers so the
   *      backend stamps `login.completed` + Mixpanel event + flips
   *      `state.userId` synchronously. We resolve fresh headers here so
   *      the just-updated Cognito session is reflected.
   *   2. Emit a local `data-widget-clear` so `TopByState` reverts the
   *      stage to the orb. The backend tools don't emit `clear` on
   *      success — that's our responsibility.
   *   3. Send `response.create` over the data channel with a short
   *      system note instructing the model to retry the originally
   *      blocked tool. The narrator returned from the auth-gate already
   *      told the model what to retry; this nudge is a belt-and-
   *      suspenders trigger in case the model is waiting on user audio.
   */
  const notifyLoginCompleted = useCallback(async () => {
    const a = activeAttemptRef.current;
    const ctx = loginContextRef.current;
    // D-48 follow-up #8: capture blockedToolName BEFORE clearing the
    // ref so the release `response.create` can name the tool explicitly
    // and pin `tool_choice` to it.
    const blockedToolName = ctx.blockedToolName;
    const fresh = await resolveAuthHeaders().catch(() => ({} as Record<string, string>));
    authHeadersRef.current = { ...authHeadersRef.current, ...fresh };
    void postVoiceAuthPromote({ apiBaseUrl: apiBaseUrlRef.current,
      conversationId,
      durationMs: ctx.loginGateMs ? Date.now() - ctx.loginGateMs : undefined,
      blockedToolName,
      headersToInject: authHeadersRef.current,
    });
    setLastWidgetEvent({ type: 'data-widget-clear', data: {} });
    // Do NOT wipe the chronological scroll list here. Per decisions/078
    // ("older widgets persisting is the load-bearing win"), prior widgets
    // — including the just-completed prompt-login card — should remain in
    // the scroll history so the user can still see what happened. Wiping
    // also caused a parity regression vs the post-end text-chat replay
    // (the replay reads from Mastra and showed every widget; the live
    // overlay was missing all pre-login widgets after OTP verified).
    loginContextRef.current = {};
    if (a) {
      // D-48 follow-up #8: live call `e6841c60-...` showed the model
      // would respond to the previous vague release ("continue with the
      // action they originally asked for") by VERBALLY apologizing —
      // "I can't complete the order lookup right this moment" — instead
      // of retrying `lookup_orders`. The previous release had two
      // weaknesses: (1) it didn't name the tool, so the model had to
      // remember what was blocked across many turns; (2) it left
      // `tool_choice` at `auto`, so the model was free to choose
      // "explain instead of act". This release now NAMES the tool in
      // the prompt AND pins `tool_choice` to that specific function,
      // closing the apology escape hatch. Falls back to `auto` only if
      // `blockedToolName` wasn't captured (the gate fires from
      // `BaseConciergeTool.checkVoiceAuthGate` so this should always be
      // populated in practice, but defensiveness is cheap).
      const toolChoice: 'auto' | { type: 'function'; name: string } = blockedToolName
        ? { type: 'function', name: blockedToolName }
        : 'auto';
      const instructions = blockedToolName
        ? `The shopper just finished signing in. Immediately call \`${blockedToolName}\` again with the SAME arguments as before. Do NOT explain. Do NOT apologize. Do NOT re-greet. Do NOT say you can't help — you CAN now, they're signed in. Speak only after the tool returns.`
        : 'The shopper just finished signing in. Immediately call the tool you were going to call before the login prompt, with the same arguments. Do NOT explain or apologize — the user is signed in now. Speak only after the tool returns.';
      sendOrQueueResponseCreate(a, {
        type: 'response.create',
        response: {
          instructions,
          tool_choice: toolChoice,
        },
      });
    }
  }, [conversationId, sendOrQueueResponseCreate]);

  /**
   * D-48 follow-up: paired with `notifyLoginCompleted` — fired by the
   * widget when `inlineVerifyOtp` fails. We previously sent only a
   * tightly constrained `tool_choice:'none'` filler response.create
   * after `submit_otp_for_login(confirmed:true)`, so without this
   * callback the model would stay silent forever after a verify
   * failure (Cognito 400) — the widget UI shows the error inline but
   * the user gets no audio feedback. Now we send an unconstrained
   * response.create with explicit "tell the user the code didn't
   * match" instructions so the model breaks the silence and prompts
   * for a re-read. The widget itself stays on the OTP step locally so
   * the user can correct without going through the phone form again.
   */
  const sendText = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (!text) return;
      const a = activeAttemptRef.current;
      if (!a?.dc || a.dc.readyState !== 'open' || phase !== 'live') return;

      if (a.responseInFlight) {
        safeSendDataChannel(a.dc, { type: 'response.cancel' });
      }

      safeSendDataChannel(a.dc, {
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{ type: 'input_text', text }],
        },
      });
      sendOrQueueResponseCreate(a, { type: 'response.create' });

      // D-93: a sent message MUST yield a `response.created`; arm the watchdog
      // so a dead-but-connected session is caught from the text path too.
      armLiveness(a);

      partialRef.current.user = undefined;
      upsertLine('user', text, { partial: false });
    },
    [armLiveness, phase, safeSendDataChannel, sendOrQueueResponseCreate, upsertLine],
  );

  const notifyLoginFailed = useCallback(
    (reason: string) => {
      const a = activeAttemptRef.current;
      if (!a) return;
      const safeReason = (reason || 'verification failed').replace(/["\n]/g, ' ').slice(0, 200);
      sendOrQueueResponseCreate(a, {
        type: 'response.create',
        response: {
          instructions:
            `The verification code didn't match (reason: ${safeReason}). ` +
            'Gently tell the user the code was wrong and ask them to read the six digits again — they can still see the OTP form on screen. ' +
            'Do NOT re-prompt for the phone number, do NOT call any tools yet. Wait for the user to read the digits aloud.',
        },
      });
    },
    [sendOrQueueResponseCreate],
  );

  return {
    phase,
    muted,
    seconds,
    lines,
    error,
    errorTitle,
    isAssistantSpeaking,
    isAssistantAudible,
    isUserSpeaking,
    toolCallsInFlight,
    lastWidgetEvent,
    widgetEvents,
    lastStatusEvent,
    setMuted,
    end,
    retry,
    notifyLoginCompleted,
    notifyLoginFailed,
    sendText,
  };
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/**
 * Map a `getUserMedia` rejection to a friendly, actionable message.
 *
 * Classifies by the stable `DOMException.name` web-standard enum — NOT the
 * free-text `.message`, which varies per browser/locale and is cryptic
 * ("Permission denied", "The request is not allowed…"). Returns a short
 * `title` for the error card headline + a longer actionable `body`.
 */
function describeMicError(err: unknown): { title: string; body: string } {
  const name = err instanceof Error ? err.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return {
        title: 'Microphone access needed',
        body: 'Voice mode needs your microphone. Allow microphone access for this site in your browser settings, then try again.',
      };
    case 'NotFoundError':
    case 'OverconstrainedError':
      return {
        title: 'No microphone found',
        body: "We couldn't find a microphone. Connect one, then try again.",
      };
    case 'NotReadableError':
    case 'AbortError':
      return {
        title: 'Microphone unavailable',
        body: 'Your microphone is unavailable — it may be in use by another app. Close anything else using it, then try again.',
      };
    default:
      return {
        title: 'Microphone unavailable',
        body: "We couldn't start your microphone. Please try again.",
      };
  }
}

function waitForDataChannelOpen(dc: RTCDataChannel, timeoutMs: number): Promise<void> {
  if (dc.readyState === 'open') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Data channel open timeout'));
    }, timeoutMs);
    const onOpen = () => {
      clearTimeout(timer);
      dc.removeEventListener('open', onOpen);
      resolve();
    };
    dc.addEventListener('open', onOpen);
  });
}

/**
 * Dev-only console logger for every event that flows in or out of the
 * voice call surface — both the `oai-events` data channel AND the
 * `/voice/tool` HTTP round-trip. Off by default; enable per-tab via:
 *
 *     localStorage.setItem('logVoiceChat', 'true')
 *
 * Disable with `localStorage.removeItem('logVoiceChat')`. The flag is
 * read on every event so toggling does not require a page reload.
 *
 * Output format (one log entry per event):
 *
 *     OPENAI - response.done
 *     {
 *       "type": "response.done",
 *       …,
 *       "timestamp": "03:45:12:983 PM PDT"
 *     }
 *
 * Direction prefixes (color-coded in the header for fast scanning):
 *  - `OPENAI`  (green)  — RECEIVED from OpenAI on the data channel
 *  - `BROWSER` (blue)   — SENT by the FE (data channel OR HTTP request)
 *  - `BACKEND` (purple) — RECEIVED from our api-rewards backend (HTTP response)
 *
 * `category` is a short label rendered after the dash:
 *  - data channel events: pass the event `type` (e.g. `response.done`)
 *  - HTTP tool round-trip: pass `tool request - <name>` / `tool response - <name>`
 *
 * Strict no-op when the flag isn't set or `localStorage` is unavailable
 * (SSR, Safari private mode), so it's safe to leave call sites in
 * production builds.
 */
type VoiceLogDirection = 'OPENAI' | 'BROWSER' | 'BACKEND' | 'VAD';
const VOICE_LOG_COLORS: Record<VoiceLogDirection, string> = {
  OPENAI: '#10B981',
  BROWSER: '#3B82F6',
  BACKEND: '#8B5CF6',
  VAD: '#F59E0B',
};

function fmtVoiceLogTimestamp(d: Date = new Date()): string {
  const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
  const h12 = d.getHours() % 12 || 12;
  const hh = String(h12).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  let tz = '';
  try {
    const parts = d.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ');
    tz = parts[parts.length - 1] ?? '';
  } catch {
    tz = '';
  }
  return `${hh}:${mm}:${ss}:${ms} ${ampm}${tz ? ` ${tz}` : ''}`;
}

function logVoiceEvent(direction: VoiceLogDirection, category: string, payload: unknown): void {
  let enabled = false;
  try {
    enabled = typeof localStorage !== 'undefined' && localStorage.getItem('logVoiceChat') === 'true';
  } catch {
    return;
  }
  if (!enabled) return;
  const header = `${direction} - ${category}`;
  const bodyObj =
    payload && typeof payload === 'object'
      ? { ...(payload as Record<string, unknown>), timestamp: fmtVoiceLogTimestamp() }
      : { value: payload, timestamp: fmtVoiceLogTimestamp() };
  let body: string;
  try {
    body = JSON.stringify(bodyObj, null, 2);
  } catch {
    body = '[unserializable payload]';
  }
  const headerStyle = `color: ${VOICE_LOG_COLORS[direction]}; font-weight: 700`;
  const bodyStyle = 'color: inherit; font-weight: 400; font-family: ui-monospace, Menlo, Consolas, monospace';
  // eslint-disable-next-line no-console
  console.log(`%c${header}%c\n${body}`, headerStyle, bodyStyle);
}
